import React from 'react';
import { EFFECT_ICONS } from './effectIcons';
import { EFFECT_MENU, EFFECT_SPECS } from './effectSpecs';
import { createItemId } from '../state/timelineModel';

/**
 * Adding an effect.
 *
 * It writes an item onto the Effects layer bound to the selected clip. It does
 * not call the backend: an effect is now part of the edit, not a new asset
 * baked from it, so it can be adjusted, retimed and undone like anything else
 * on the timeline. The apply-and-bake path still exists on the Media tab for
 * producing a reusable clip.
 */


/**
 * Where on the clip a new effect sits.
 *
 * Most effects mean "for this whole shot". A fade does not: it has its own
 * `duration`, and it belongs at one end of the clip -- the head for a fade in,
 * the tail for a fade out. Spanning the clip made the timeline chip claim 0→8s
 * for a one-second fade, and stacked it exactly on top of every other effect on
 * that clip so only the last one added was visible.
 */
export const spanForEffect = (type, params, clip) => {
    if (type !== 'fade') return { start: clip.start, end: clip.end };
    const length = Math.min(Math.max(Number(params?.duration) || 1, 0.1), clip.duration);
    return params?.fadeType === 'out'
        ? { start: clip.end - length, end: clip.end }
        : { start: clip.start, end: clip.start + length };
};

function AddEffect({ clip, disabled, onAdd }) {
    const canAdd = Boolean(clip) && !disabled;

    return (
        <div className="nle-add-effect">
            <span className="nle-add-effect-label">
                {clip ? 'Add to this clip' : 'Select a clip to add an effect'}
            </span>
            <div className="nle-add-effect-buttons">
                {EFFECT_MENU.map((type) => (
                    <button
                        key={type}
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={!canAdd}
                        title={EFFECT_SPECS[type].label}
                        onClick={() => {
                            const params = { ...EFFECT_SPECS[type].defaults };
                            const { start, end } = spanForEffect(type, params, clip);
                            onAdd({
                                id: createItemId('fx'),
                                type,
                                label: EFFECT_SPECS[type].label,
                                clipId: clip.id,
                                params,
                                start,
                                end,
                                duration: end - start,
                                readOnly: false,
                            });
                        }}
                    >
                        {(() => { const Icon = EFFECT_ICONS[type]; return Icon ? <Icon size={16} aria-hidden="true" /> : null; })()}
                        <span className="nle-add-effect-name">{EFFECT_SPECS[type].label}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}

export default AddEffect;
